import { InstructorReference, LocationReference, PrivateLesson, PrivateLessonOrder } from '../types/GrooverTypes'

/**
 * Creates a validated instructor reference object
 * @param id - The instructor's user ID
 * @param name - The instructor's display name
 * @returns InstructorReference object with id and name
 * @throws Error if id or name is empty
 */
export function createInstructorReference(id: string, name: string): InstructorReference {
  if (!id || !name) {
    throw new Error('Instructor reference requires both id and name')
  }
  return { id, name }
}

/**
 * Creates a validated location reference object
 * @param id - The location's ID
 * @param name - The location's name
 * @returns LocationReference object with id and name
 * @throws Error if id or name is empty
 */
export function createLocationReference(id: string, name: string): LocationReference {
  if (!id || !name) {
    throw new Error('Location reference requires both id and name')
  }
  return { id, name }
}

/**
 * Gets instructor ID from a PrivateLesson or PrivateLessonOrder with fallback to legacy field
 * @param lessonOrOrder - The PrivateLesson or PrivateLessonOrder object
 * @returns The instructor ID, or undefined if not present
 */
export function getInstructorId(lessonOrOrder: PrivateLesson | PrivateLessonOrder): string | undefined {
  if ('instructor' in lessonOrOrder && lessonOrOrder.instructor) {
    return lessonOrOrder.instructor.id
  }
  if ('defaultInstructor' in lessonOrOrder && lessonOrOrder.defaultInstructor) {
    return lessonOrOrder.defaultInstructor.id
  }
  return lessonOrOrder.instructorId
}

/**
 * Gets instructor name from a PrivateLesson or PrivateLessonOrder with fallback to legacy field
 * @param lessonOrOrder - The PrivateLesson or PrivateLessonOrder object
 * @returns The instructor name, or the ID as fallback, or undefined if not present
 */
export function getInstructorName(lessonOrOrder: PrivateLesson | PrivateLessonOrder): string | undefined {
  if ('instructor' in lessonOrOrder && lessonOrOrder.instructor) {
    return lessonOrOrder.instructor.name
  }
  if ('defaultInstructor' in lessonOrOrder && lessonOrOrder.defaultInstructor) {
    return lessonOrOrder.defaultInstructor.name
  }
  if ('instructorName' in lessonOrOrder && lessonOrOrder.instructorName) {
    return lessonOrOrder.instructorName
  }
  // Fallback to ID if name not available
  return lessonOrOrder.instructorId
}

/**
 * Gets location ID from a PrivateLesson or PrivateLessonOrder with fallback to legacy field
 * @param lessonOrOrder - The PrivateLesson or PrivateLessonOrder object
 * @returns The location ID, or undefined if not present
 */
export function getLocationId(lessonOrOrder: PrivateLesson | PrivateLessonOrder): string | undefined {
  if ('location' in lessonOrOrder && lessonOrOrder.location) {
    return lessonOrOrder.location.id
  }
  if ('defaultLocation' in lessonOrOrder && lessonOrOrder.defaultLocation) {
    return lessonOrOrder.defaultLocation.id
  }
  // locationId only exists on PrivateLesson, not PrivateLessonOrder
  if ('locationId' in lessonOrOrder) {
    return lessonOrOrder.locationId
  }
  return undefined
}

/**
 * Gets location name from a PrivateLesson or PrivateLessonOrder with fallback to legacy field
 * @param lessonOrOrder - The PrivateLesson or PrivateLessonOrder object
 * @returns The location name, or the ID as fallback, or undefined if not present
 */
export function getLocationName(lessonOrOrder: PrivateLesson | PrivateLessonOrder): string | undefined {
  if ('location' in lessonOrOrder && lessonOrOrder.location) {
    return lessonOrOrder.location.name
  }
  if ('defaultLocation' in lessonOrOrder && lessonOrOrder.defaultLocation) {
    return lessonOrOrder.defaultLocation.name
  }
  // locationId only exists on PrivateLesson, not PrivateLessonOrder
  // Fallback to ID if name not available
  if ('locationId' in lessonOrOrder) {
    return lessonOrOrder.locationId
  }
  return undefined
}

/**
 * Gets default instructor ID from a PrivateLessonOrder with fallback to legacy field
 * @param order - The PrivateLessonOrder object
 * @returns The default instructor ID, or undefined if not present
 */
export function getDefaultInstructorId(order: PrivateLessonOrder): string | undefined {
  return order.defaultInstructor?.id || order.instructorId
}

/**
 * Gets default instructor name from a PrivateLessonOrder with fallback to legacy field
 * @param order - The PrivateLessonOrder object
 * @returns The default instructor name, or undefined if not present
 */
export function getDefaultInstructorName(order: PrivateLessonOrder): string | undefined {
  return order.defaultInstructor?.name || order.instructorName
}

/**
 * Removes deprecated legacy fields from a PrivateLesson object
 * This ensures the object can be saved to Firestore without violating security rules
 * @param lesson - The PrivateLesson object (may contain legacy fields)
 * @returns A new PrivateLesson object without legacy fields
 */
export function removeLegacyFields(lesson: PrivateLesson): PrivateLesson {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { instructorId, locationId, instructorName, ...cleanLesson } = lesson as any
  return cleanLesson as PrivateLesson
}
